/*
 * Copyright (c) 2024 CalAmp Corp.  All Rights Reserved
 */
import {useCallback, useState} from 'react';
import {error} from '@ion/core/src/logger/logger';
import {getJob, removeDevice} from '@ion/core/src/services/Connect';
import {Device, DownloadJobType, Job, JobConflictResolution} from '@ion/core/src/types/entities';
import {formatDeviceQueryString, JoinFormat} from './JobConflictUtils';

export type Conflict = {
  jobId: number;
  id: number;
  task: number;
  devices: {
    deviceIdentifier: string;
    deviceName: string;
    deviceType: string;
    esn: string;
    id: number;
    status: string;
  }[];
};

interface JobConflictResolverProps {
  selectedOptions: { [key: string]: string };
  accountId: number;
  conflicts: Conflict[];
  devices: Device[];
}

const useJobConflictResolver = ({
  selectedOptions,
  accountId,
  conflicts,
  devices
}: JobConflictResolverProps) => {
  const {CANCEL}                      = JobConflictResolution;
  const [isCanceling, setIsCanceling] = useState(false);
  /**
   * @cancelDeviceConflicts
   * 
   * Cancels the devices that are in conflict based on the selected conflict resolution
   * within the JobConflictResolution component.
   * 
   * @param jobId - The job id that contains the conflicting devices.
   * @param devices - The devices that are in conflict.
   */
  const cancelDeviceConflicts = async(jobId: number, devices: Device[], taskIndex: number) => {
    try {
      const job = await getJob(jobId) as Job;
      const {task} = job.tasks?.[taskIndex];
      task.progressStatus = DownloadJobType.Cancelling;
      task.criteria = [{
        memberSearch : {
          searchTerms : {
            account : accountId.toString(),
            esn     : formatDeviceQueryString(devices, JoinFormat.COMMA)
          },
          hierLevel : 'ALL'
        }
      }];
      await removeDevice(job);
    } catch (err) {
      error('Failed to cancel conflicting devices', err);
    }
  };

  const cancelSelectedConflicts = useCallback(async() => {
    setIsCanceling(true);
    for (const conflictGroup of conflicts) {
      const conflictsToCancel = conflictGroup.filter(conflict =>
        selectedOptions[`${conflict?.id}-${conflict?.task}`] === CANCEL
      );
      for (const conflict of conflictsToCancel) {
        const conflictDevices = conflict?.devices?.map?.(conflictDevice =>
          devices?.find(device =>
            (device?.esn === conflictDevice?.deviceIdentifier ||
             device?.serialNumber === conflictDevice?.deviceIdentifier)
          )
        );
        try {
          await cancelDeviceConflicts(conflict?.id, conflictDevices, conflict?.task);
        } catch (err) {
          error('Failed to cancel selected conflicts', err);
          break;
        }
      }
    }
    setIsCanceling(false);
  }, [conflicts, selectedOptions, devices]);
  
  return {
    cancelSelectedConflicts,
    cancelDeviceConflicts,
    selectedOptions,
    isCanceling
  };
};

export default useJobConflictResolver;
